import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import useTranslations from "@/shared/lang/useTranslations";

import { countryName, flagFromCountryCode } from "../constants/countries.constants";
import {
  TIER_INFO,
  kycLevelToTier,
  kycStatusMeta,
  resolveIdentityDisplayName,
} from "../constants/identityTiers.constants";
import type { Identity } from "../types/identities-api.types";
import { HolderAvatar } from "./HolderAvatar";

type HolderCardProps = {
  identity: Identity;
  onOpen: (identityId: string) => void;
};

export const HolderCard = ({ identity, onOpen }: HolderCardProps) => {
  const { t } = useTranslations("cards");
  const tier = kycLevelToTier(identity.kyc_level);
  const tierInfo = TIER_INFO[tier];
  const kyc = kycStatusMeta(identity.kyc_status);

  const displayName = resolveIdentityDisplayName(identity);
  const document = identity.identification_number?.trim() || "—";
  const rawCountry = identity.nationality?.trim() || identity.address?.country?.trim() || "";
  const countryFlag = rawCountry ? flagFromCountryCode(rawCountry) : "";
  const countryLabel = rawCountry ? countryName(rawCountry) : "";

  const cardsCount = typeof identity.cards_count === "number" ? identity.cards_count : 0;
  const maxCards = tierInfo.maxCards;
  const usagePct = Number.isFinite(maxCards)
    ? Math.min(100, (cardsCount / Math.max(1, maxCards)) * 100)
    : Math.min(100, cardsCount * 10);
  const maxLabel = Number.isFinite(maxCards) ? maxCards : "∞";

  return (
    <button
      type="button"
      onClick={() => onOpen(identity.id)}
      className="flex flex-col gap-4 rounded-[var(--radius-lg)] border border-border bg-card p-5 text-left transition-all hover:border-ring/40 hover:shadow-[var(--shadow-elev)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    >
      <div className="flex items-start justify-between gap-3">
        <HolderAvatar name={displayName} tier={tier} verified={kyc.group === "approved"} size={48} />
        <ColocaBadge variant={kyc.tone}>{t(kyc.labelKey as never)}</ColocaBadge>
      </div>

      <div className="min-w-0">
        <div className="truncate text-[15px] font-semibold text-foreground">{displayName}</div>
        {identity.email && (
          <div className="mt-0.5 truncate text-xs text-muted-foreground">{identity.email}</div>
        )}
        <div className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground">
          <span className="font-mono">{document}</span>
          {countryLabel && (
            <>
              <span className="text-muted-foreground/60">·</span>
              <span className="inline-flex items-center gap-1">
                {countryFlag && (
                  <span aria-hidden="true" className="text-sm leading-none">
                    {countryFlag}
                  </span>
                )}
                <span>{countryLabel}</span>
              </span>
            </>
          )}
        </div>
      </div>

      <div className="border-t border-dashed border-border pt-3">
        <div className="flex items-center justify-between text-[11px] uppercase tracking-wider text-muted-foreground">
          <span>{t("identities_label_cards")}</span>
          <span className="font-mono normal-case tracking-normal text-foreground">
            {cardsCount} {t("identities_label_cards_of")} {maxLabel}
          </span>
        </div>
        <div className="relative mt-1.5 h-1 overflow-hidden rounded-full bg-muted">
          <div
            className="absolute inset-y-0 left-0 rounded-full"
            style={{ width: `${usagePct}%`, background: tierInfo.color }}
          />
        </div>
      </div>
    </button>
  );
};
